import { env } from '@core/config/env';
import { removeToken, handleApiResponse } from '@core/index';
import { ModulePermission } from '@modules/admin/types';
import { ApiResponse } from '@shared/interface';

// API Response Types
export interface GoogleAuthUrl {
  url: string;
}

export type GoogleAuthUrlResponse = ApiResponse<GoogleAuthUrl>;

export interface GoogleAuthRequest {
  code: string;
  state: string;
}

export interface User {
  id: number;
  name: string;
  email: string;
  isGoogleLoggedIn: boolean;
  userProfileImage: string | null;
  modules: ModulePermission[];
}

export interface GameInfo {
  game_id: number;
  game_name: string;
  game_type: string;
  status: boolean;
  image: null | string;
  internal_name: string;
  game_key: string;
  platform_id: number;
}

export interface AuthResponse {
  token: string;
  user: User;
  games: GameInfo[];
  isLoggedInFirstTime: boolean;
}

/**
 * Fetches the Google OAuth URL for authentication
 * @param isLogin - Whether this is a login flow (true) or signup flow (false)
 * @returns Promise containing the Google OAuth URL
 */
export const getGoogleAuthUrl = async (isLogin: boolean = true): Promise<GoogleAuthUrlResponse> => {
  const response = await fetch(`${env.apiBaseUrl}/auth/google/url?isLogin=${isLogin}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': '69420',
    },
  });

  return handleApiResponse<GoogleAuthUrlResponse>(
    response,
    'Failed to get Google authentication URL',
    { handle401: false }, // Don't auto-logout on auth endpoints
  );
};

/**
 * Authenticates user with Google using authorization code and state
 * @param authData - Object containing code and state from OAuth callback
 * @returns Promise containing authentication token and user data
 */
export const authenticateWithGoogle = async (authData: GoogleAuthRequest): Promise<ApiResponse<AuthResponse>> => {
  const response = await fetch(`${env.apiBaseUrl}/auth/google`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': '69420',
    },
    body: JSON.stringify(authData),
  });

  return handleApiResponse<ApiResponse<AuthResponse>>(
    response,
    'Authentication failed. Please try again.',
    { handle401: false }, // Don't auto-logout on auth endpoints
  );
};

export const clearTokenAndRedirect = () => {
  removeToken();
  window.location.href = '/login';
};
